import { useEffect } from "react";
import { PriceUI } from "./PriceUI";
import { StandardProps } from "@/components/componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { useMonetateId } from "@/hooks/useMonetateId/useMonetateId";
import type {
  Price,
  PriceRange,
  ReductionHistory,
  ReductionHistoryRange,
} from "@/types/prodcatBaseTypes";
import { isPerMetre } from "@/utils/product/isPerMetre";
import { isM2MRomanBlindOrCurtain } from "@/utils/product/isMadeToMeasure";
import { getColourGroupForVariant } from "@/utils/product/getColourGroupForVariant";
import { getPromoMessaging } from "@/utils/product/getPromoMessaging";
import { getPromotionalMessagesToShow } from "@/utils/product/getPromotionalMessagesToShow.ts";
import { isDiorBrand } from "@JohnLewisPartnership/jl-design-system/dist/utils/brands/brands";
import { recordImpressionByParam } from "@JohnLewisPartnership/experiments-utils";
import { hasWholeNumberPrice } from "./utils/checkWholeNumberPrice";
import { SimpleProductVariantGroup } from "@/types/types.ts";

function getPriceValueOrRange(
  priceData: { max: string; min: string } | string | undefined,
) {
  if (typeof priceData === "object") {
    return priceData.max === priceData.min
      ? priceData.min
      : {
          from: priceData.min,
          to: priceData.max,
        };
  }
  return priceData || "";
}

function getReductionHistoryItem(
  reductionHistory: Array<ReductionHistoryRange | ReductionHistory>,
  chronology: number,
) {
  const historyItem = reductionHistory?.find(
    (item) => item.chronology === chronology,
  );
  return getPriceValueOrRange(historyItem?.display);
}

function convertVariantPriceRangeToFormattedPrice(
  variantPriceRange:
    | PriceRange
    | Price
    | SimpleProductVariantGroup["variantPriceRange"]
    | undefined,
) {
  return variantPriceRange
    ? {
        was: getReductionHistoryItem(variantPriceRange.reductionHistory, 0),
        then1: getReductionHistoryItem(variantPriceRange.reductionHistory, 1),
        then2: getReductionHistoryItem(variantPriceRange.reductionHistory, 2),
        now: getPriceValueOrRange(variantPriceRange.display),
      }
    : undefined;
}

export function Price({
  product,
  configProps,
  flags,
  runtimeConfig,
  experiencesRawObject,
}: Pick<
  StandardProps,
  "product" | "configProps" | "flags" | "runtimeConfig" | "experiencesRawObject"
>) {
  const { variantPriceRange } = product;
  const { variant, isSelected } = useVariant(product);
  const { monetateId } = useMonetateId();

  const variantGroupsDefault = Boolean(configProps?.useVariantGroups ?? true);
  //todo: useNewTypography flag added temporary until after A/B test is complete, then we can remove the useNewTypography flag and just use isResponsiveStylesEnabled
  const isResponsiveStylesEnabled =
    flags.useNewTypography.value === true ||
    flags.isResponsiveStylesEnabled.value === true;
  const redesignedPromoPrices = flags.redesignedPromoPrices.value === true;

  let priceRange:
    | PriceRange
    | Price
    | SimpleProductVariantGroup["variantPriceRange"]
    | undefined;

  if (variantGroupsDefault && !isSelected) {
    const variantGroup = getColourGroupForVariant(
      product.variantGroups,
      variant.id,
    );
    const variantGroupPriceRange = variantGroup?.variantPriceRange;

    priceRange = variantGroupPriceRange || variantPriceRange;
  } else {
    priceRange = isSelected ? variant.price : variantPriceRange;
  }

  const brand = product.brand?.name || "";
  const formattedPrice = convertVariantPriceRangeToFormattedPrice(priceRange);

  const priceSuffix =
    isPerMetre(product) || isM2MRomanBlindOrCurtain(product)
      ? "per metre"
      : undefined;

  const { pageMessages } = getPromoMessaging(
    getPromotionalMessagesToShow(
      product,
      variant,
      isSelected,
      variantGroupsDefault && product.variantGroups
        ? getColourGroupForVariant(product.variantGroups, variant.id)
        : undefined,
    ),
  );

  const isPricePromiseWithHistory =
    redesignedPromoPrices &&
    !!formattedPrice?.was &&
    pageMessages[0]?.promotionType === "Price match price established";

  const isPriceMatchNonPriceEstablished =
    !isDiorBrand(product.brand?.name) &&
    pageMessages.find(
      (pm) =>
        pm.promotionType === "Price match non price established" &&
        !pm.title.includes("on selected"),
    ) !== undefined;

  useEffect(() => {
    if (hasWholeNumberPrice(priceRange)) {
      recordImpressionByParam(
        runtimeConfig?.monetateEnvironment,
        experiencesRawObject,
        "pdp-scaffold-priceWithZeroDecimal",
        "true",
        monetateId,
      );
    }
  }, [variant.id]);

  return (
    <PriceUI
      price={formattedPrice}
      priceSuffix={priceSuffix}
      brand={brand}
      isResponsiveStylesEnabled={isResponsiveStylesEnabled}
      redesignedPromoPrices={redesignedPromoPrices}
      isPricePromiseWithHistory={isPricePromiseWithHistory}
      highlightPriceMatchNonPriceEstablished={isPriceMatchNonPriceEstablished}
    />
  );
}
